import type { CyanGoalPlan, CyanGoalStep } from "./types";

export type CapabilityState = "available" | "degraded" | "missing" | "unknown";

export type RecoveryMode = "none" | "automatic" | "owner_required";

export interface CapabilityEvidence {
  capability: string;
  state: CapabilityState;
  provider?: string;
  worker?: string;
  reason?: string;
}

export interface StepCapabilityResolution {
  stepId: string;
  requiredCapabilities: string[];
  availableCapabilities: string[];
  degradedCapabilities: string[];
  missingCapabilities: string[];
  unknownCapabilities: string[];
  selectedWorkers: string[];
  recoveryMode: RecoveryMode;
  ownerActionRequired: boolean;
  readyForExecution: boolean;
}

export interface GoalCapabilityResolution {
  goalId: string;
  steps: StepCapabilityResolution[];
  allCapabilitiesResolved: boolean;
  ownerActionRequired: boolean;
}

function unique(values: string[]): string[] {
  return [...new Set(values)];
}

export function resolveStepCapabilities(
  step: CyanGoalStep,
  evidence: readonly CapabilityEvidence[],
): StepCapabilityResolution {
  const required = unique(step.capabilities);

  const available: string[] = [];
  const degraded: string[] = [];
  const missing: string[] = [];
  const unknown: string[] = [];
  const workers: string[] = [];

  for (const capability of required) {
    const match = evidence.find((item) => item.capability === capability);

    if (!match) {
      unknown.push(capability);
      continue;
    }

    if (match.worker) {
      workers.push(match.worker);
    }

    switch (match.state) {
      case "available":
        available.push(capability);
        break;

      case "degraded":
        degraded.push(capability);
        break;

      case "missing":
        missing.push(capability);
        break;

      case "unknown":
        unknown.push(capability);
        break;
    }
  }

  const unresolvedCount = missing.length + unknown.length;

  const ownerActionRequired = missing.length > 0;

  const recoveryMode: StepCapabilityResolution["recoveryMode"] =
    missing.length > 0
      ? "owner_required"
      : unknown.length > 0 || degraded.length > 0
        ? "automatic"
        : "none";

  return {
    stepId: step.id,
    requiredCapabilities: required,
    availableCapabilities: available,
    degradedCapabilities: degraded,
    missingCapabilities: missing,
    unknownCapabilities: unknown,
    selectedWorkers: unique(workers),
    recoveryMode,
    ownerActionRequired,
    readyForExecution: unresolvedCount === 0 && degraded.length === 0,
  };
}

export function resolveGoalCapabilities(
  plan: CyanGoalPlan,
  evidence: readonly CapabilityEvidence[],
): GoalCapabilityResolution {
  const steps = plan.steps.map((step) =>
    resolveStepCapabilities(step, evidence),
  );

  return {
    goalId: plan.goal.id,
    steps,

    allCapabilitiesResolved: steps.every((step) => step.readyForExecution),

    ownerActionRequired: steps.some((step) => step.ownerActionRequired),
  };
}
